import React, { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import ResourceCard from "@features/resources/components/ResourceCard";
import FindAvailableTime from "@features/search/components/FindAvailableTime";
import { useOutlookStore, useResourceStore, useSettingStore, useAppointmentStore } from "@shared/stores";
import NavigateSearch from "@features/search/components/NavigateSearch";
import VStack from "@components/layout/VStack";
import HStack from "@components/layout/HStack";
import useScrollShadow from "@shared/hooks/useScrollShadow";
import { Loader } from "@components/ui/Loader";
import TitlePage from "@components/layout/TitlePage";
import { defaultPagination, titlePages } from "@shared/constants/appConstants";
import ResourceTypeToolbar from "@components/ui/ResourceTypeToolbar";
import { DeskLocationEntity, ResourceEntity } from "@features/resources/services/types/resourceEntity";
import { useScrollLoadMore } from "@shared/hooks/useScrollLoadMore";
import { paramsKey } from "@/pages/taskpane/shared/constants/paramsKey";
import { useGetResourcesRecommended } from "../../resources/services/useGetResourcesRecommended";
import NoResultFound from "../components/NoResultFound";
import { useSearchParams } from "react-router-dom";

const Recommendation = () => {
  const { t } = useTranslation();
  const { defaultResourceType, defaultLocations, defaultCategoryIds } = useSettingStore();
  const selectedResource = useResourceStore((state) => state.selectedResource);
  const { organizer, recurringDates, allAttendees, isNoEndDateRecurring } = useOutlookStore();
  const { startDateTimeUtc, endDateTimeUtc, timeZone } = useAppointmentStore();

  const listRef = useRef<HTMLDivElement | null>(null);

  const [searchParams] = useSearchParams();
  const resourceType = useMemo(() => {
    return searchParams.get(paramsKey.resourceType) || defaultResourceType;
  }, [searchParams, defaultResourceType]);

  const locationIdsString = useMemo(() => {
    return defaultLocations.defaultResourceTypes.find((i) => i.type === resourceType)?.locationIds || "";
  }, [defaultLocations.defaultResourceTypes, resourceType]);
  const locationIds = useMemo(() => {
    if (!locationIdsString) return [];
    return locationIdsString.split(",").map(Number).sort();
  }, [locationIdsString]);

  const payload = useMemo(
    () => ({
      pageSize: defaultPagination.pageSize,
      organizer: organizer || "",
      resourceType: resourceType || "",
      locationIds,
      timeZoneStandardName: timeZone || "",
      attendees: [...allAttendees.map((att) => att.Email), organizer],
      occurrences:
        recurringDates?.length > 0
          ? recurringDates.map((date) => ({
              utcStartDate: date.startDate || "",
              utcEndDate: date.endDate || "",
            }))
          : [
              {
                utcStartDate: startDateTimeUtc || "",
                utcEndDate: endDateTimeUtc || "",
              },
            ],
      isNoEndDate: recurringDates?.length > 0 ? isNoEndDateRecurring : null,
      isRecurring: recurringDates?.length > 0,
      categoryIds: defaultCategoryIds ? defaultCategoryIds.split(",").map(Number) : [],
    }),
    [
      organizer,
      resourceType,
      locationIds,
      recurringDates,
      startDateTimeUtc,
      endDateTimeUtc,
      timeZone,
      allAttendees,
      isNoEndDateRecurring,
      defaultCategoryIds,
    ]
  );

  const {
    data: resourcesDataInfinite,
    fetchNextPage,
    isFetching,
    isError,
  } = useGetResourcesRecommended({
    payload,
  });

  const resourceList = useMemo(() => {
    const allResources = resourcesDataInfinite?.pages?.flatMap((page) => page.resources) || [];
    const allDeskLocations = resourcesDataInfinite?.pages?.flatMap((page) => page.deskLocations) || [];
    const deskLocationList: DeskLocationEntity[] = [];
    allDeskLocations.forEach((location) => {
      if (!deskLocationList.some((item) => item.locationId === location.locationId)) {
        deskLocationList.push(location);
      }
    });
    return allResources.filter(
      (r: ResourceEntity) =>
        !selectedResource.some((c) => c.id === r.id) &&
        !r.isDisableResourceByHotDeskRule(selectedResource, deskLocationList)
    );
  }, [resourcesDataInfinite, selectedResource]);

  useScrollLoadMore({
    containerRef: listRef,
    hasMore: !isError && !resourcesDataInfinite?.pages[resourcesDataInfinite?.pages?.length - 1]?.isLastPage,
    isLoading: isFetching,
    onLoadMore: () => {
      fetchNextPage();
    },
    threshold: 200,
  });

  useScrollShadow(listRef);

  // ✅ Reset scroll position when search params change (date, resource type, location)
  // This ensures users always see results from the top when switching filters
  useEffect(() => {
    if (listRef.current) {
      // console.log("🔝 Resetting scroll position due to param changes");
      listRef.current.scrollTop = 0;
    }
  }, [startDateTimeUtc, endDateTimeUtc, timeZone, resourceType, locationIds.join(",")]);

  // useEffect(() => {
  //   return () => {
  //     queryClient.setQueriesData(
  //       { queryKey: [QUERY_KEY.GET_RESOURCES, QUERY_KEY.GET_RESOURCES_RECOMMENDED] },
  //       (oldData: any) => {
  //         if (!oldData?.pages) return oldData;
  //         return {
  //           ...oldData,
  //           pages: oldData.pages.slice(0, 1),
  //           pageParams: oldData.pageParams?.slice(0, 1),
  //         };
  //       }
  //     );
  //   };
  // }, [queryClient]);

  return (
    <VStack className="h-full w-full" justifyContent="between">
      <VStack className="px-4 pt-1 pb-3 flex-1 overflow-hidden" gap={3}>
        {/* Title */}
        <TitlePage title={t(titlePages.recommendation.title)} description={t(titlePages.recommendation.description)} />

        {/* Resource Type */}
        <HStack className="w-full" justifyContent="between" gap={2}>
          <ResourceTypeToolbar />
        </HStack>

        {/* Resource List */}
        <VStack ref={listRef} className={`overflow-y-auto no-scrollbar flex-1`} gap={3}>
          {resourceList?.length > 0 ? (
            resourceList.map((resource: ResourceEntity) => {
              return (
                <div className="w-full" key={resource.id}>
                  <ResourceCard resource={resource} />
                </div>
              );
            })
          ) : isFetching ? null : (
            <NoResultFound resourceType={resourceType} />
          )}

          {/* Loading indicator */}
          {isFetching && (
            <HStack className="w-full justify-center p-4">
              <Loader texts={[t("Loading...")]} />
            </HStack>
          )}
        </VStack>

        {/* Find Available Time */}
        <FindAvailableTime />
      </VStack>

      {/*  */}
      <NavigateSearch />
    </VStack>
  );
};

export default Recommendation;
